import React from "react";
import Reveal from "./Reveal";
import FAQ from "./FAQ";

const CARDS = [
  {
    title: "Become a Partner",
    forWhom:
      "Training institutes, universities, consulting firms, and educational entrepreneurs.",
    bullets: [
      "License to deliver AI+ certifications",
      "Co-branded delivery",
      "Marketing support",
      "Trainer certification",
      "Revenue share",
    ],
    cta: "Apply to become a partner",
  },
  {
    title: "Become a Trainer",
    forWhom:
      "Individual practitioners who want to facilitate AI For Everyone certifications.",
    bullets: [
      "Trainer certification",
      "Delivery rights",
      "Community of trainers",
      "Continuous content updates",
    ],
    cta: "Apply to become a trainer",
  },
  {
    title: "Co-develop a Certification",
    forWhom:
      "Universities, industry bodies, and institutions building specialized AI capabilities.",
    bullets: [
      "Co-branded certification",
      "Joint IP",
      "Shared marketing",
      "Long-term partnership",
    ],
    cta: "Explore co-development",
  },
];

const PARTNER_FAQS = [
  {
    q: "How does the partner program work?",
    a: "Authorized Partners license the AI For Everyone IP and deliver our certifications in their cities, countries, and continents under a co-branded model. We provide curriculum, trainer certification, marketing support, and continuous updates. Partners run delivery and share revenue.",
  },
  {
    q: "What is The Practitioner Method?",
    a: "The Practitioner Method™ is our proprietary three-tier journey (AI Native → AI Builder → AI Leader) built across six pillars and four audiences. Every certification ends in a shippable artifact, not a quiz score.",
  },
  {
    q: "How does AI For Everyone differ from Coursera or upGrad?",
    a: "AI For Everyone is a four-layer ecosystem — Learn, Build, Hire, Invest — not just a course catalogue. We train practitioners, run the Innovation Arena to mentor builders, connect talent to AI roles, and back the founders who emerge. Every layer feeds the next.",
  },
  {
    q: "What certifications does AI For Everyone offer?",
    a: "We've launched 11 certifications across foundations, builder tracks, function-specific applications (Sales, Marketing, HR, Finance, Legal), and leadership (Chief AI Officer). More verticals — healthcare, government, manufacturing — launch in Phase 2 from Q3 2026.",
  },
];

export default function Partners() {
  return (
    <section
      id="partners"
      className="py-10 md:py-14 border-t hairline"
      data-testid="partners-section"
    >
      <div className="container-edge">
        <Reveal>
          <p className="section-number">12 — PARTNER WITH US</p>
        </Reveal>
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <Reveal>
              <h2
                className="font-display text-[34px] sm:text-[42px] lg:text-[52px] leading-[1.05] tracking-tight"
                style={{ fontWeight: 400 }}
              >
                Bring AI For Everyone to <em className="italic" style={{ fontWeight: 400 }}>your region.</em>
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-5 flex items-end">
            <Reveal delay={120}>
              <p className="text-[17px] md:text-[19px] leading-relaxed" style={{ color: "var(--ink-secondary)" }}>
                We don't deliver alone. Authorized Partners license our IP and deliver our
                certifications in their cities, countries, and continents. Trainers get certified
                to facilitate. This is how AI For Everyone scales worldwide — without losing quality.
              </p>
            </Reveal>
          </div>
        </div>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
          {CARDS.map((c, i) => (
            <Reveal key={c.title} delay={i * 80}>
              <article
                className="card-edge h-full p-8 lg:p-10"
                data-testid={`partner-card-${i + 1}`}
              >
                <p className="mono-label-gold">CARD {String(i + 1).padStart(2, "0")}</p>
                <h3
                  className="mt-4 font-display text-[26px] tracking-tight"
                  style={{ fontWeight: 500 }}
                >
                  {c.title}
                </h3>
                <p
                  className="mt-3 italic text-[14px]"
                  style={{ color: "var(--ink-secondary)" }}
                >
                  {c.forWhom}
                </p>
                <ul className="mt-6 space-y-2">
                  {c.bullets.map((b) => (
                    <li
                      key={b}
                      className="text-[14px] pl-4 relative"
                      style={{ color: "var(--ink-secondary)" }}
                    >
                      <span
                        className="absolute left-0 top-[10px] w-2 h-px"
                        style={{ background: "var(--accent-gold)" }}
                      />
                      {b}
                    </li>
                  ))}
                </ul>
                <a
                  href="#enquiry"
                  className="mt-8 inline-block text-[14px] link-underline text-ink"
                  data-testid={`partner-cta-${i + 1}`}
                >
                  {c.cta} →
                </a>
              </article>
            </Reveal>
          ))}
        </div>

        <Reveal delay={260}>
          <FAQ items={PARTNER_FAQS} testIdPrefix="partner-faq" theme="light" />
        </Reveal>
      </div>
    </section>
  );
}
